import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import {
    AppBar,
    Box,
    Button,
    Dialog,
    IconButton,
    InputAdornment,
    MenuItem,
    Stack,
    TextField,
    Toolbar as MuiToolbar,
    Typography,
    useTheme,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import StepHeader from "./StepHeader";
import FrostedCard from "./FrostedCard";
import KayttokohdeField from "./KayttokohdeField";
import KayttoIkaToggle from "./KayttoIkaToggle";
import RasitusluokkaField from "./RasitusluokkaField";
import SpecGuideModal from "./SpecGuideModal";
import SpecRow from "./SpecRow";
import WizardEyebrow from "./WizardEyebrow";
import { amberCtaSx } from "./amberCtaSx";
import { parsePlan } from "src/utils/laskuri/planModel";
import { estimateLeftoverLitres } from "src/utils/quote/leftoverEstimate";
import { captureError } from "src/utils/errorReporting";
import {
    lujuusluokkaOptions,
    raekokoOptions,
    notkeusOptions,
    LUJUUS_ID_MUU,
    RAE_KOKO_ID_MUU,
    NOTKEUS_ID_MUU,
    KAYTTO_IKA_ID_100V,
    RAE_KOKO_IDS_FORBIDDEN_ON_LONG_LINE,
} from "src/utils/quote/quoteConstants";
import {
    calcKestoHoursFromM3,
    isRaekokoForbiddenByLine,
    PUMP_LINE_RAEKOKO_LIMIT_M,
} from "src/utils/quote/pumppuKesto";
import { useLang, useT, useTRich } from "src/i18n";
import { formatNumber, localizeDecimal } from "src/utils/valut/valutFormatters";

// Chunk-load failure (stale deploy, offline) must degrade the dialog, not crash the
// wizard. The fallback is still rendered inside the app's LanguageProvider (it only
// replaces FloorPlanCalculator's own tree, mounted via <Suspense> further down), so
// it can call useT() like any other component.
function ChunkLoadFallback() {
    const t = useT();
    return (
        <Typography sx={{ color: "error.main", p: 3 }}>
            {t("wizard.stepConcrete.calculatorLoadFailed")}
        </Typography>
    );
}

const FloorPlanCalculator = lazy(() =>
    import("src/components/laskuri/FloorPlanCalculator").catch((err) => {
        captureError(err, { tags: { entity: "laskuri", operation: "chunk.load", swallowed: true } });
        return { default: ChunkLoadFallback };
    })
);

const unitAdornment = (unit) => (
    <InputAdornment position="end">
        <Box sx={{ fontSize: 20, fontWeight: 600, color: "text.secondary", letterSpacing: "0.02em" }}>
            {unit}
        </Box>
    </InputAdornment>
);

const linkButtonSx = {
    textTransform: "none",
    p: 0,
    minWidth: 0,
    "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
};

export default function StepConcrete({ draft, errors, onChange }) {
    const theme = useTheme();
    const amber = theme.custom?.wizard?.amber ?? "#E8893B";
    const amberLight = theme.custom?.wizard?.amberLight ?? "#FFB36B";
    const t = useT();
    const tRich = useTRich();
    const { lang } = useLang();
    const lujuusOptions = useMemo(() => lujuusluokkaOptions(t), [t]);
    const raekokoOpts = useMemo(() => raekokoOptions(t), [t]);
    const notkeusOpts = useMemo(() => notkeusOptions(t), [t]);

    const [specGuideOpen, setSpecGuideOpen] = useState(false);
    const [specGuideMountKey, setSpecGuideMountKey] = useState(0);

    // Auto-focus the amount (m³) field when arriving on this step with it empty —
    // it's the first and load-bearing input. rAF defers the focus until after the
    // Fade-in paint so it lands reliably. Mount-only: typing must not re-grab focus,
    // and returning with a value already set skips it. On mobile this raises the
    // numeric keypad, which is the intended next action here.
    const maaraRef = useRef(null);
    useEffect(() => {
        if (!String(draft.maaraM3 ?? "").trim()) {
            const id = requestAnimationFrame(() => maaraRef.current?.focus());
            return () => cancelAnimationFrame(id);
        }
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function openSpecGuide() {
        setSpecGuideMountKey((n) => n + 1);
        setSpecGuideOpen(true);
    }

    const [laskuriOpen, setLaskuriOpen] = useState(false);
    const [laskuriMountKey, setLaskuriMountKey] = useState(0);

    // spec rule: guided-spec writes must never clobber a stored floor plan.
    function preserveFloorPlan(nextSelvitys) {
        const floorPlan = draft.betoniLaatuSelvitys?.floorPlan;
        if (!floorPlan) return nextSelvitys;
        return { ...(nextSelvitys || {}), floorPlan };
    }

    function openLaskuri() {
        setLaskuriMountKey((n) => n + 1); // fresh mount per open (SpecGuideModal convention)
        setLaskuriOpen(true);
    }

    function handleLaskuriApply({ plan, result }) {
        onChange({
            maaraM3: String(result.m3Recommended),
            kesto: calcKestoHoursFromM3(result.m3Recommended),
            betoniLaatuSelvitys: { ...(draft.betoniLaatuSelvitys || {}), floorPlan: plan },
        });
        setLaskuriOpen(false);
    }

    const update = (key) => (e) => onChange({ [key]: e.target.value });

    function handleMaaraM3Change(e) {
        const next = e.target.value;
        // kesto always re-derives from m³; manual edits are overwritten on the
        // next m³ keystroke by design — m³ is the load-bearing input.
        onChange({ maaraM3: next, kesto: calcKestoHoursFromM3(next) });
    }

    function handleKayttokohdeChange(e) {
        const next = e.target.value;
        const patch = { kayttokohde: next };
        // Changing käyttökohde re-opens the avustin to re-resolve the spec for the new
        // context. Reset specMode + the persisted trail (the resolved class may no
        // longer fit the new käyttökohde), but PRESERVE the user's guided answers so the
        // re-opened avustin is pre-filled — re-confirming is one click ("Käytä nämä"),
        // not a redo. The resolved spec values also stay in the draft, recoverable via
        // "Tiedän tarkan betonilaadun" / the avustin. (Previously guidedAnswers was wiped
        // here too, silently throwing away the user's avustin work on a single tap.)
        if (draft.specMode != null) {
            patch.specMode = null;
            patch.betoniLaatuSelvitys = preserveFloorPlan(null);
        }
        onChange(patch);
        if (next) openSpecGuide();
    }

    function handleSpecGuideApply(result) {
        // Modal already shapes `result` as the full patch (specMode + 6 fields).
        onChange({ ...result, betoniLaatuSelvitys: preserveFloorPlan(result.betoniLaatuSelvitys) });
        setSpecGuideOpen(false);
    }

    function switchToManual() {
        onChange({ specMode: "manual", betoniLaatuSelvitys: preserveFloorPlan({ specMode: "manual" }) });
    }

    const isLineTooLong = Number(draft.linjanPituus) > PUMP_LINE_RAEKOKO_LIMIT_M;
    const raekokoConflict = isRaekokoForbiddenByLine(draft.raeKokoId, draft.linjanPituus);
    const raekokoHelper = (errors.raeKokoId && t(errors.raeKokoId))
        || (raekokoConflict && t("wizard.validation.raekokoConflict"))
        || "";

    // Rasitusluokka is still free text; the rest match betoni* "Muu" rows.
    const anyMuu =
        draft.lujuusId === LUJUUS_ID_MUU ||
        draft.raeKokoId === RAE_KOKO_ID_MUU ||
        draft.notkeusId === NOTKEUS_ID_MUU ||
        draft.rasitusluokka === "Muu";

    const isGuided = draft.specMode === "guided";
    // Required-field affordance: tint the m³ outline amber while empty, matching
    // the osoite / käyttökohde empty-required cue. The :not(.Mui-error) guard lets
    // the red error border win once validation has fired.
    const maaraEmpty = !String(draft.maaraM3 ?? "").trim();

    // Surplus-concrete note: part of every load stays unpumpable in the boom pipe
    // / pump, so the ordered (delivered) amount always exceeds what reaches the
    // pour. The residual is a length-based estimate from the pump lengths chosen
    // on the previous step (draft.puomi / draft.linjanPituus) — hidden when both
    // are unset ("don't know"). Exact figure is confirmed by the provider later.
    const leftover = estimateLeftoverLitres(draft);
    const leftoverLead =
        leftover.boom > 0 && leftover.line > 0
            ? t("wizard.stepConcrete.leftoverBoth")
            : leftover.boom > 0
                ? t("wizard.stepConcrete.leftoverBoom")
                : t("wizard.stepConcrete.leftoverLinja");
    // Locale-aware: a hardcoded `.replace(".", ",")` here leaked "0,4 m³" into
    // the English UI.
    const leftoverM3 = formatNumber(leftover.total / 1000, lang, 1);
    // Litres deliberately does NOT go through formatNumber. It is always a whole
    // number (the estimate rounds to *50 / *10), so there is no separator to
    // localize — but Intl WOULD add thousands grouping, turning the Finnish
    // "1790" into "1 790". A 60 m boom with a 200 m line already exceeds 1000 l,
    // so that is an ordinary case, not an edge one, and Finnish must not change.
    const leftoverLitres = localizeDecimal(leftover.total, lang);

    // Draft-boundary corrupt-plan guard. parsePlan is a total function (null on
    // corrupt/unsupported, never throws), so a silent null here would hide a real
    // anomaly: a draft floorPlan was written from our own validated calculator
    // output, so failing to re-parse means a serialize/store roundtrip bug, an
    // unhandled version, or tampering — worth a Sentry report. (The calculator's
    // own localStorage boundary in FloorPlanCalculator stays silent — arbitrary
    // scratch state there is not anomalous.) Memoized on the raw value so it
    // reports once per distinct draft, not every render.
    const rawFloorPlan = draft.betoniLaatuSelvitys?.floorPlan;
    const parsedFloorPlan = useMemo(() => {
        const parsed = parsePlan(rawFloorPlan);
        if (rawFloorPlan && !parsed) {
            captureError(new Error("Draft floorPlan failed to parse (corrupt or unsupported version)"), {
                tags: { entity: "laskuri", operation: "draft.parse", swallowed: true },
            });
        }
        return parsed;
    }, [rawFloorPlan]);

    const specSelects = (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
                label={t("wizard.stepConcrete.lujuusLabel")}
                select
                fullWidth
                value={draft.lujuusId}
                onChange={update("lujuusId")}
            >
                {lujuusOptions.map((opt) => (
                    <MenuItem key={opt.label} value={opt.value}>{opt.label}</MenuItem>
                ))}
            </TextField>
            <TextField
                label={t("wizard.stepConcrete.raekokoLabel")}
                select
                fullWidth
                value={draft.raeKokoId}
                onChange={update("raeKokoId")}
                error={raekokoConflict || !!errors.raeKokoId}
                helperText={raekokoHelper}
            >
                {raekokoOpts.map((opt) => (
                    <MenuItem
                        key={opt.label}
                        value={opt.value}
                        disabled={isLineTooLong && RAE_KOKO_IDS_FORBIDDEN_ON_LONG_LINE.includes(opt.value)}
                    >
                        {opt.label}
                    </MenuItem>
                ))}
            </TextField>
            <TextField
                label={t("wizard.stepConcrete.notkeusLabel")}
                select
                fullWidth
                value={draft.notkeusId}
                onChange={update("notkeusId")}
            >
                {notkeusOpts.map((opt) => (
                    <MenuItem key={opt.label} value={opt.value}>{opt.label}</MenuItem>
                ))}
            </TextField>
        </Stack>
    );

    const heroInputSx = {
        "& .MuiOutlinedInput-input": {
            fontFamily: "'Inter', sans-serif",
            fontSize: { xs: 28, sm: 34 },
            fontWeight: 700,
            py: 1.25,
            letterSpacing: "-0.02em",
        },
    };

    return (
        <Stack spacing={2}>
            <StepHeader>{t("wizard.stepConcrete.title")}</StepHeader>
            <FrostedCard
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderColor: `${amber}55`,
                    boxShadow: `inset 0 0 0 1px ${amber}22`,
                }}
            >
                <WizardEyebrow color="text.secondary" sx={{ fontSize: 14, letterSpacing: "0.18em", mb: 1 }}>
                    {t("wizard.stepConcrete.amountSectionTitle")}
                </WizardEyebrow>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                        placeholder={t("wizard.stepConcrete.amountPlaceholder")}
                        type="number"
                        fullWidth
                        required
                        inputRef={maaraRef}
                        value={draft.maaraM3}
                        onChange={handleMaaraM3Change}
                        slotProps={{
                            htmlInput: { min: 1, step: 0.5, "aria-label": t("wizard.stepConcrete.amountAriaLabel") },
                            input: { endAdornment: unitAdornment("m³") },
                        }}
                        error={!!errors.maaraM3}
                        helperText={errors.maaraM3 ? t(errors.maaraM3) : t("wizard.stepConcrete.amountHelper")}
                        sx={{
                            ...heroInputSx,
                            ...(maaraEmpty ? {
                                "& .MuiOutlinedInput-root:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
                                    borderColor: amber,
                                },
                            } : {}),
                        }}
                    />
                    <TextField
                        placeholder={t("wizard.stepConcrete.kestoPlaceholder")}
                        type="number"
                        fullWidth
                        value={draft.kesto}
                        onChange={update("kesto")}
                        slotProps={{
                            htmlInput: { min: 0, step: 0.25, "aria-label": t("wizard.stepConcrete.kestoAriaLabel") },
                            input: { endAdornment: unitAdornment("h") },
                        }}
                        helperText={t("wizard.stepConcrete.kestoHelper")}
                        sx={heroInputSx}
                    />
                </Stack>
                <Button
                    startIcon={<SquareFootIcon />}
                    onClick={openLaskuri}
                    size="small"
                    sx={{ mt: 1.5, color: amberLight, textTransform: "none" }}
                >
                    {draft.betoniLaatuSelvitys?.floorPlan
                        ? t("wizard.stepConcrete.editFloorPlan")
                        : t("wizard.stepConcrete.drawFloorPlan")}
                </Button>

                {leftover.total > 0 && (
                    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mt: 1.5 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 18, mt: "1px", color: amberLight, flexShrink: 0 }} />
                        <Box sx={{ fontSize: 13, lineHeight: 1.55, color: "text.secondary" }}>
                            {tRich("wizard.stepConcrete.leftoverBody", {
                                lead: leftoverLead,
                                amount: (
                                    <Box component="strong" sx={{ color: "text.primary", fontWeight: 600 }}>
                                        {t("wizard.stepConcrete.leftoverAmount", { litres: leftoverLitres, m3: leftoverM3 })}
                                    </Box>
                                ),
                            })}
                        </Box>
                    </Box>
                )}
            </FrostedCard>

            <KayttokohdeField
                value={draft.kayttokohde}
                onChange={handleKayttokohdeChange}
                error={!!errors.kayttokohde}
                helperText={errors.kayttokohde ? t(errors.kayttokohde) : undefined}
                required
            />

            {draft.kayttokohde && (
                <>
                    {draft.specMode == null && (
                        <FrostedCard sx={{ p: { xs: 2, sm: 2.5 } }}>
                            <Stack spacing={1.5} sx={{ alignItems: "stretch" }}>
                                <Button
                                    variant="contained"
                                    color="warning"
                                    size="large"
                                    startIcon={<AutoAwesomeIcon />}
                                    onClick={openSpecGuide}
                                    sx={amberCtaSx(theme)}
                                >
                                    {t("wizard.stepConcrete.helpMeChoose")}
                                </Button>
                                <Box sx={{
                                    textAlign: "center",
                                    color: "text.secondary",
                                    fontSize: 12,
                                    letterSpacing: "0.16em",
                                    textTransform: "uppercase",
                                }}>
                                    {t("wizard.stepConcrete.or")}
                                </Box>
                                <Button
                                    variant="text"
                                    onClick={switchToManual}
                                    sx={{
                                        textTransform: "none",
                                        color: "text.secondary",
                                        fontWeight: 500,
                                        "&:hover": { color: "text.primary", bgcolor: "transparent", textDecoration: "underline" },
                                    }}
                                >
                                    {t("wizard.stepConcrete.iKnowExact")}
                                </Button>
                            </Stack>
                        </FrostedCard>
                    )}

                    {isGuided && (
                        <FrostedCard tone="success" sx={{ p: 2 }}>
                            <WizardEyebrow color="text.secondary" sx={{ mb: 1 }}>
                                {t("wizard.stepConcrete.guidedResultTitle")}
                            </WizardEyebrow>
                            <Stack spacing={0.5}>
                                <SpecRow label={t("wizard.shared.rasitusluokkaLabel")} value={draft.rasitusluokka} />
                                <SpecRow
                                    label={t("wizard.shared.suunnitteluikaLabel")}
                                    value={draft.kayttoIkaId === KAYTTO_IKA_ID_100V ? t("wizard.shared.years100") : t("wizard.shared.years50")}
                                />
                            </Stack>
                            <Stack direction="row" spacing={2.5} sx={{ mt: 1.5, mb: 2 }}>
                                <Button
                                    size="small"
                                    onClick={openSpecGuide}
                                    sx={{ ...linkButtonSx, color: amber, fontWeight: 500 }}
                                >
                                    {t("wizard.stepConcrete.reopenGuide")}
                                </Button>
                                <Button
                                    size="small"
                                    onClick={switchToManual}
                                    sx={{ ...linkButtonSx, color: "text.secondary" }}
                                >
                                    {t("wizard.stepConcrete.switchToManual")}
                                </Button>
                            </Stack>
                            {specSelects}
                            <Box sx={{ fontSize: 12, color: "text.secondary", fontStyle: "italic", lineHeight: 1.55, mt: 1.5 }}>
                                {t("wizard.quoteConstants.betoniLaatuDisclaimer")}
                            </Box>
                        </FrostedCard>
                    )}

                    {draft.specMode === "manual" && (
                        <>
                            <Box>
                                <Box sx={{ fontSize: 13, color: "text.secondary", display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
                                    <AutoAwesomeIcon sx={{ fontSize: 14, color: amber, opacity: 0.85 }} />
                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={openSpecGuide}
                                        sx={{
                                            background: "none",
                                            border: 0,
                                            p: 0,
                                            color: amber,
                                            cursor: "pointer",
                                            fontFamily: "inherit",
                                            fontSize: "inherit",
                                            "&:hover": { textDecoration: "underline" },
                                        }}
                                    >
                                        {t("wizard.stepConcrete.useGuideAnyway")}
                                    </Box>
                                </Box>
                                <RasitusluokkaField
                                    value={draft.rasitusluokka}
                                    onChange={(v) => onChange({ rasitusluokka: v })}
                                />
                            </Box>
                            {specSelects}
                            <KayttoIkaToggle
                                value={draft.kayttoIkaId}
                                onChange={(v) => onChange({ kayttoIkaId: v })}
                            />
                        </>
                    )}

                    {draft.specMode != null && (
                        <TextField
                            label={t("wizard.stepConcrete.descriptionLabel")}
                            fullWidth
                            multiline
                            minRows={2}
                            value={draft.betonikuvaus}
                            onChange={update("betonikuvaus")}
                            helperText={anyMuu
                                ? t("wizard.stepConcrete.descriptionHelperMuu")
                                : t("wizard.stepConcrete.descriptionHelper")}
                        />
                    )}
                </>
            )}

            <SpecGuideModal
                key={specGuideMountKey}
                open={specGuideOpen}
                onClose={() => setSpecGuideOpen(false)}
                kayttokohde={draft.kayttokohde}
                initialAnswers={draft.guidedAnswers}
                initialKayttoIkaId={draft.kayttoIkaId}
                onApply={handleSpecGuideApply}
            />

            <Dialog fullScreen open={laskuriOpen} onClose={() => setLaskuriOpen(false)}>
                <AppBar position="relative" color="transparent" elevation={0} sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <MuiToolbar variant="dense">
                        <Typography variant="h6" sx={{ flex: 1 }}>{t("wizard.stepConcrete.calculatorTitle")}</Typography>
                        <IconButton edge="end" onClick={() => setLaskuriOpen(false)} aria-label={t("common.dialog.close")}>
                            <CloseIcon />
                        </IconButton>
                    </MuiToolbar>
                </AppBar>
                <Box sx={{ flex: 1, minHeight: 0, p: { xs: 1, sm: 2 } }}>
                    {laskuriOpen && (
                        <Suspense fallback={null}>
                            <FloorPlanCalculator
                                key={laskuriMountKey}
                                initialPlan={parsedFloorPlan}
                                onResult={handleLaskuriApply}
                                actionLabel={t("wizard.stepConcrete.useAmount")}
                                allowFullscreen={false}
                            />
                        </Suspense>
                    )}
                </Box>
            </Dialog>
        </Stack>
    );
}
